/**
 * Pure presentation helpers for membership PLANS (the catalog + purchase
 * surfaces). Every number a plan shows — price, visit_limit — is rendered
 * verbatim from the server; nothing here computes money, discounts, or
 * eligibility (that is all backend-owned).
 *
 * The billing CADENCE now comes from the SERVER: both VenuePlanResponse and
 * SubscriptionResponse carry a `billing_interval` token
 * ("monthly" | "quarterly" | "annual") derived by
 * `common.utils.billing_interval_for_plan_type`. This module maps that token to
 * the display strings (a "/mo" suffix, the disclosure adverb) — pure
 * presentation, never money math. The plan_type is retained only as a SAFE
 * FALLBACK for older rows that predate `billing_interval` (or an unresolved
 * plan_type); the FE no longer re-derives the plan_type→cadence business rule.
 */

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export type PlanFraming = "subscribe" | "pass";

export interface PlanTypeDisplay {
  /** "subscribe" (unlimited plans) | "pass" (visits_card). Drives the CTA verb. */
  framing: PlanFraming;
  /** Cadence noun ("month" | "quarter" | "year"), or null for an unknown type. */
  intervalNoun: string | null;
  /** Cadence adverb for the disclosure ("monthly" | … | "each period"). */
  intervalAdverb: string;
  /** Price suffix ("/mo" | "/quarter" | "/yr" | "" for unknown). */
  priceSuffix: string;
}

// Cadence token → display strings. Keyed on the SERVER `billing_interval`
// ("monthly" | "quarterly" | "annual"); the plan_type keys (incl. visits_card,
// which bills monthly) are retained so an older row with no billing_interval
// still resolves via its plan_type fallback.
const PLAN_TYPE_INTERVALS: Record<
  string,
  { noun: string; adverb: string; suffix: string }
> = {
  monthly: { noun: "month", adverb: "monthly", suffix: "/mo" },
  quarterly: { noun: "quarter", adverb: "quarterly", suffix: "/quarter" },
  annual: { noun: "year", adverb: "yearly", suffix: "/yr" },
  visits_card: { noun: "month", adverb: "monthly", suffix: "/mo" },
};

/**
 * Resolve the plan's framing (subscribe vs pass) + cadence display strings.
 *
 * `framing` is a presentation concern derived from plan_type (visits_card →
 * pass). The CADENCE strings come from the server-derived `billingInterval`
 * token, rendered verbatim; `planType` is only the safe fallback for older rows
 * that predate `billing_interval`.
 */
export function planTypeDisplay(
  planType: string,
  billingInterval?: string | null,
): PlanTypeDisplay {
  const framing: PlanFraming = planType === "visits_card" ? "pass" : "subscribe";
  // Prefer the server cadence token; fall back to plan_type for legacy rows.
  const key = billingInterval || planType;
  const interval = PLAN_TYPE_INTERVALS[key];
  if (!interval) {
    // Unknown cadence — never fabricate one. Show the price with no suffix; the
    // disclosure still tells the truth that it auto-renews.
    return {
      framing,
      intervalNoun: null,
      intervalAdverb: "each period",
      priceSuffix: "",
    };
  }
  return {
    framing,
    intervalNoun: interval.noun,
    intervalAdverb: interval.adverb,
    priceSuffix: interval.suffix,
  };
}

/** "8:00 AM" for a 24h "HH:MM"; returns the raw value if it is not parseable. */
export function accessTimeLabel(raw: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!m) return raw;
  const hour = Number(m[1]);
  const minute = m[2];
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return raw;
  const period = hour < 12 ? "AM" : "PM";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${minute} ${period}`;
}

/** "Mon", "Sun" … for a 0–6 day index; "Day N" for an out-of-range index. */
export function accessDayLabel(dayOfWeek: number): string {
  return DAY_LABELS[dayOfWeek] ?? `Day ${dayOfWeek}`;
}

/**
 * A one-line human "access window" from a plan's optional allowed_days /
 * allowed_time_start / allowed_time_end. Returns null when the plan carries NO
 * restriction (unlimited access) so the caller renders nothing — never a
 * fabricated window. Days render as short labels; a contiguous run collapses to
 * a "Mon–Fri" range for readability.
 */
export function accessWindowLabel(plan: {
  allowed_days?: number[] | null;
  allowed_time_start?: string | null;
  allowed_time_end?: string | null;
}): string | null {
  const days = plan.allowed_days ?? [];
  const start = plan.allowed_time_start?.trim();
  const end = plan.allowed_time_end?.trim();
  const hasDays = days.length > 0;
  const hasTime = Boolean(start && end);
  if (!hasDays && !hasTime) return null;

  const parts: string[] = [];
  if (hasDays) parts.push(daysLabel(days));
  if (hasTime) parts.push(`${accessTimeLabel(start as string)}–${accessTimeLabel(end as string)}`);
  return parts.join(", ");
}

/** "Mon–Fri" for a contiguous run, else "Mon, Wed, Fri". Weekday (Mon-first) order. */
function daysLabel(days: number[]): string {
  // Order Mon(1)…Sun(0) for a natural read, dedupe, keep only valid indices.
  const ordered = [...new Set(days)]
    .filter((d) => d >= 0 && d <= 6)
    .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
  if (ordered.length === 0) return "";
  // Contiguous (in the Mon-first ordering) → collapse to a range.
  const isContiguous = ordered.every((d, i) => {
    if (i === 0) return true;
    const prev = (ordered[i - 1] + 6) % 7;
    const cur = (d + 6) % 7;
    return cur === prev + 1;
  });
  if (ordered.length >= 3 && isContiguous) {
    return `${accessDayLabel(ordered[0])}–${accessDayLabel(ordered[ordered.length - 1])}`;
  }
  return ordered.map(accessDayLabel).join(", ");
}
